import { AfterViewInit, Component, OnDestroy, OnInit, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { dateFilterAgGrid } from '@lmc-app/utils/filter-functions';
import { CellClickedEvent, SideBarDef } from 'ag-grid-community';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { ExternalDashboardService } from '@lmc-app/core/services/external-dashboard.service';
import { SelectItem } from '@lmc-app/shared/components/multi-select-search/multi-select-search.component';
import { User } from '@lmc-app/shared/interface/user.interface';

@Component({
  selector: 'app-external-dashboard',
  templateUrl: './external-dashboard.component.html',
  styleUrls: ['./external-dashboard.component.scss']
})
export class ExternalDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() agencyId: string;
  @Input() viewApplicationLink: boolean;
  @Input() currentUser: User;

  public gridApi_agency: any;
  public gridApi_agency_data: any;
  public gridColumnApi_agency: any;

  agencyData: any;

  defaultColDef_agency: any;
  public columnDefs_agency = [];
  private readonly deadStageTerms = ['dead', 'declined', 'non qualified', 'cancelled', 'stale'];  // Closed stages

  // row classes based on the stages
  rowClassRules = {
    'funded-row': (params) => (params?.data?.Stage || '').toLowerCase().includes('funded'),
    'closed-stage-row': (params) => {
      const stage = (params?.data?.Stage || '').toLowerCase();
      return this.deadStageTerms.some((term) => stage.includes(term));
    },
    'active-stage-row': (params) => {
      const stage = (params?.data?.Stage || '').toLowerCase();
      if (!stage) return false;
      return !stage.includes('funded') && !this.deadStageTerms.some((term) => stage.includes(term));
    }
  };

  fundedCount: number;
  fundedAmount: any;
  showLoader: boolean;
  displayTime: Date = new Date;

  private readonly destroy$ = new Subject<void>();
  private refreshIntervalId: ReturnType<typeof setInterval>;  

  // Custom filteres
  protected referralDateSearch: Date | null = null;
  protected nameSearch = '';
  protected debtorNumberSearch = '';
  protected collectorSearch = '';
  protected creditorSearch = '';

  protected subjects = ['External Notes'];    // Get notes with subject, External Notes 

	protected stageOptions: SelectItem[] = [];
  protected selectedStages: (string | number)[] = [];

  constructor(
    private apiService: LegacyApiService,
    private router: Router,
    private _externalAgenciesService: ExternalDashboardService
  ) {};

  ngOnInit(): void {
    this.getStage();
    this.getExternalList();

    // Refetch every 5mins
    this.refreshIntervalId = setInterval(() => {
        this.getExternalList();
        this.displayTime= new Date;
    },  5 * 60 * 1000);
   }

  // Custom field behavior
  private readonly columnBehaviorByField: Record<string, any> = {
    Referral_Date: {
      filterParams: {
        comparator: (filterDate, cellValue) => dateFilterAgGrid(filterDate, cellValue),
        browserDatePickerDateFormat: 'yyyy-mm-dd',
      },
    },
    Lead_Source: {
      valueGetter: (params) => customLeadSource(params?.data?.Lead_Source),
    },
    Deal_Name: {
      cellStyle: () => {
        if (this.viewApplicationLink) {
          return { cursor: 'pointer', color: '#116abd' };
        }
        return null;
      },
    },
    Collector_Formula: {
      tooltipValueGetter: (params) => params.value,
    },
    Agency_Amount: {
      valueFormatter: (params: any) => formatCurrency(params.value),
    },
    Account_Bal: {
      valueFormatter: (params: any) => formatCurrency(params.value),
    },
  };

  // Fallback Columns, if the DB-driven config hasn't been set up yet or the request fails.
  private readonly defaultColumnConfig_agency = [
    { field: 'Referral_Date', headerName: 'Referral Date', filter: 'agDateColumnFilter', minWidth: 150, maxWidth: 150, order: 0, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Lead_Source', headerName: 'Type', filter: 'agTextColumnFilter', minWidth: 250, maxWidth: 250, order: 1, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Stage', headerName: 'Stage', filter: 'agTextColumnFilter', minWidth: 250, maxWidth: 250, order: 2, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Deal_Name', headerName: 'Name', filter: 'agTextColumnFilter', pinned: 'left', minWidth: 250, maxWidth: 250, order: 3, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Agency_Amount', headerName: 'Amount', filter: 'agNumberColumnFilter', minWidth: 130, maxWidth: 130, order: 4, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Province', headerName: 'Province', filter: 'agTextColumnFilter', minWidth: 110, maxWidth: 110, order: 5, visible: true, wrapText: false, autoHeight: false, },
    { field: 'description', headerName: 'Note', filter: 'agTextColumnFilter', minWidth: 50, maxWidth: 50, order: 6, visible: true, wrapText: true, autoHeight: true, },
    { field: 'Collector_Formula', headerName: 'Collector', filter: 'agTextColumnFilter', minWidth: 230, maxWidth: 230, order: 7, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Acc_Number', headerName: 'Account Number (EDP)', filter: 'agTextColumnFilter', minWidth: 230, maxWidth: 230, order: 8, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Creditt', headerName: 'Creditor', filter: 'agTextColumnFilter', minWidth: 250, maxWidth: 250, order: 9, visible: true, wrapText: false, autoHeight: false, },
    { field: 'Mobile', headerName: 'Mobile', filter: 'agNumberColumnFilter', minWidth: 130, maxWidth: 130, order: 10, visible: true, wrapText: false, autoHeight: false, },
  ];

  /**
   * 
   * Build Column fields based on the user requirement
   * @param columnConfig 
   * @returns 
   */
  private buildColumnDefs(columnConfig: any[]): any[] {
    return (columnConfig || [])
      .filter((col) => col?.visible !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((col) => ({
        headerName: col.headerName,
        field: col.field,
        filter: col.filter,
        minWidth: col.minWidth,
        maxWidth: col.maxWidth,
        pinned: col.pinned,
        wrapText: col.wrapText,
        autoHeight: col.autoHeight,
        ...(this.columnBehaviorByField[col.field] || {}),
      }));
  }

  /**
   * Map the fields based on the data from table else the default
   */
  loadColumnConfig(): void {
    this._externalAgenciesService.getExternalDashboardColumnConfig(this.agencyId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const config = res?.message;
        this.columnDefs_agency = this.buildColumnDefs(
          Array.isArray(config) && config.length ? config : this.defaultColumnConfig_agency
        );
      },
      error: () => {
        this.columnDefs_agency = this.buildColumnDefs(this.defaultColumnConfig_agency);
      },
    });
  }

  ngAfterViewInit(): void {
    this.loadColumnConfig();
    this.defaultColDef_agency = {
      resizable: true
    };
  }

  // Agency Data
  getExternalList(){
    this._externalAgenciesService.getExternalAgencyList(this.agencyId, this.currentUser).pipe(takeUntil(this.destroy$)).subscribe(((res: any) => {
      this.agencyData = res?.message;
      if (this.agencyData === false) {
        console.log("ZOHO error!");
      } else {
        this.agencyData.forEach((row: any) => this.getNotes(row.id, row, this.subjects));
        this.refreshGrid();
        this.totalAmountCalculations();
      }
    }))
  }

  // Re-applies the Stage filter and the top-of-grid search fields on top of agencyData and pushes the result into the grid. 
  refreshGrid(): void {
    let data = this.agencyData || [];

    if (this.selectedStages.length) {
      data = data.filter((row: any) => this.selectedStages.includes(row.Stage));
    }
    if (this.referralDateSearch) {
      const term = dayjs(this.referralDateSearch).format('YYYY-MM-DD');
      data = data.filter((row: any) => row.Referral_Date === term);
    }
    if (this.nameSearch) {
      const term = this.nameSearch.toLowerCase();
      data = data.filter((row: any) => (row.Deal_Name || '').toLowerCase().includes(term));
    }
    if (this.debtorNumberSearch) {
      const term = this.debtorNumberSearch.toLowerCase();
      data = data.filter((row: any) => (row.Acc_Number || '').toString().toLowerCase().includes(term));
    }
    if (this.collectorSearch) {
      const term = this.collectorSearch.toLowerCase();
      data = data.filter((row: any) => (row.Collector_Formula || '').toLowerCase().includes(term));
    }
    if (this.creditorSearch) {
      const term = this.creditorSearch.toLowerCase();
      data = data.filter((row: any) => (row.Creditt || '').toLowerCase().includes(term));
    }

    this.gridApi_agency_data = data;
    this.gridApi_agency?.setRowData(this.gridApi_agency_data);
  }

  // multi-select stage search
  onStageFilterChange(selected: SelectItem[]): void {
    this.selectedStages = selected.map((item) => item.id);
    this.refreshGrid();
  }

  // Clear the Stage filter and the top-of-grid search fields on Reset Filters of custom table
  onResetFilters(): void {
    this.selectedStages = [];
    this.referralDateSearch = null;
    this.nameSearch = '';
    this.debtorNumberSearch = '';
    this.collectorSearch = '';
    this.creditorSearch = '';
    this.refreshGrid();
  }

  /**
   * application -note
   * @param applicationId 
   * @param row 
   * @param subjects 
   */
  getNotes(applicationId: string, row: any, subjects: string[]){
    this._externalAgenciesService.getExternalAgencyNotes(applicationId, subjects).pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (res) {
        const latestExternalNote = Array.isArray(res) ? res[0] : res;
        row.description = latestExternalNote
          ? `${latestExternalNote.description ?? ''}${latestExternalNote.note ? ': ' + latestExternalNote.note : ''}${latestExternalNote.created_time ? ' - ' + dayjs(latestExternalNote.created_time).format('YYYY-MM-DD hh:mm A') : ''}`
          : '';
        this.refreshGrid();
      }
    })
  }

  onGridReady(params: any): void {
    this.gridApi_agency = params.api;
    this.showLoader = true;
    this.gridColumnApi_agency = params.columnApi;
    this.refreshGrid();
  }

  onCellClicked(e: CellClickedEvent) {
    if(!this.viewApplicationLink) return;
    if (e.column.getColId() === 'Deal_Name'){      
      const { id } = e.data;
      const url = this.router.serializeUrl(this.router.createUrlTree(['/applications/summary', id]));
      window.open(url, '_blank');
    }
  }

  async getStageMetrics(fieldName: string) {
    return new Promise((resolve, reject) => {
      const filteredList = [];
      this.gridApi_agency.forEachNodeAfterFilterAndSort((rowNode: any, index: any) => {
        if (rowNode?.data?.[fieldName] === 'Funded') {
          filteredList.push(rowNode?.data);
        }
      });
      let resultObject = {};
      resultObject = { count: filteredList?.length, total: (filteredList.length === 0) ? 0 : filteredList.map(item => item?.Agency_Amount)?.reduce((prev, next) => parseFloat(prev) + parseFloat(next))?.toFixed(2) };
      resolve(resultObject);
    })

  }

  onFirstDataRendered(): void {
    if (this.gridApi_agency) {
      this.totalAmountCalculations();
    }
  }

  async totalAmountCalculations() {
    let resultTotal = await this.getStageMetrics('Stage').then((item) => {
      this.fundedCount = item['count'];
      this.fundedAmount = item['total'];
    })
  }

  getStage(){
    this.apiService.getModuleField('Deals', 'Stage').pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.stageOptions = (res?.pick_list_values || []).map((stage: any) => ({
        id: stage.display_value,
        name: stage.display_value,
      }));
		});
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshIntervalId);
    this.destroy$.next();
    this.destroy$.complete();
  }
}

const formatCurrency = (value: any): string => {
  const amount = parseFloat(value);
  if (isNaN(amount)) return '';
  return amount.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

const customLeadSource=(leadSource:string)=>{
  if(leadSource === 'Rapid Program - Internal'){
    return 'Rapid Program - Internal';
  }else if(leadSource === 'Rapid Program - Portal')
    return 'Rapid';
  else {
    return 'Internal';
  }
}
